import { Button, Dialog, DialogActions, DialogBody, DialogSurface, DialogTitle } from "@fluentui/react-components";
import { Dismiss24Regular, Money24Regular, MoneySettings16Filled, MoneySettings20Filled } from "@fluentui/react-icons";
import { EventType, LogComponent, LogElement, LogTarget } from "../../../models/LogModel";
import { EventTypeEnum, OpenNotificationPanelEvent } from "../../../models/Event";
import { IIconProps, IconButton } from "@fluentui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getActiveAnomalies, getAnomalyDescription } from "../../../utils/AnomalyDetectionUtils";

import CommonConstants from "../../../models/constants/CommonConstants";
import CostCard from "../../common/costCard/CostCard";
import { DataSourceEnum } from "../../../models/NotificationModels";
import DrillDownMenu from "./CategoryDrilldownMenu";
import FeedbackPanel from "../../feedback/FeedbackPanel";
import HC_heatmap from "highcharts/modules/heatmap";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { IFinOpsBudgetAllocationCombined } from "../../../models/FinOpsBudgets";
import { Section } from "../../common/Section";
import ServiceDrillDownLayout from "../BudgetTracking/ServiceDrillDownLayout";
import ServiceLayout from "../BudgetTracking/ServiceLayout";
import budgetTrackingSytles from "../BudgetTracking/BudgetTracking.less";
import { currencyFormatter } from "../../../utils/currency";
import html2canvas from "html2canvas";
import moment from "moment";
import styles from "./CategoryHeatmap.less";
import { trackEventCallback } from "../../../utils/AppInsights";
import { useBoolean } from "@fluentui/react-hooks";
import useEventBus from "../../../hooks/useEventBus";
import { useFlights } from "../../../hooks/useSettings";
import { useGetAnomaliesByDataSourceQuery } from "../../../hooks/useNotificationQuery";
import { useMsal } from "@azure/msal-react";

// Initialize the heatmap module
HC_heatmap(Highcharts);

const feedbackIconProps: IIconProps = { iconName: "Feedback" };
const closeIconProps: IIconProps = { iconName: "Dismiss24Regular" };

interface CategoryTypeHeatmapProps {
  data: IFinOpsBudgetAllocationCombined[];
  isloadingBudgetData: boolean;
  onDrilldownSelect?: (selection: { category: string; type: string }) => void;
}

const CategoryTypeHeatmap: React.FC<CategoryTypeHeatmapProps> = ({ data, isloadingBudgetData, onDrilldownSelect }) => {
    const [selected, setSelected] = useState<{ category: string; type: string } | null>(null);
    const [isServiceLayoutOpen, setIsServiceLayoutOpen] = useState(false);

  // Extract unique category and type values
  const categories = Array.from(new Set(data.map(d => d.category ?? "Unknown")));
  const types = Array.from(new Set(data.map(d => d.type ?? "Unknown")));

  // Aggregate data by category-type
  const aggregated = new Map<string, { category: string; type: string; cost: number; budget: number }>();
  data.forEach(d => {
    const category = d.category ?? "Unknown";
    const type = d.type ?? "Unknown";
    const key = `${category}-${type}`;
    if (!aggregated.has(key)) {
      aggregated.set(key, { category, type, cost: 0, budget: 0 });
    }
    const entry = aggregated.get(key)!;
    entry.cost += d.cost ?? 0;
    entry.budget += d.budget ?? 0;
  });

  // Format data into Highcharts heatmap structure: [x, y, value]
  const heatmapData = Array.from(aggregated.values()).map(d => {
    const x = types.indexOf(d.type);
    const y = categories.indexOf(d.category);
    const ratio = d.budget > 0 ? d.cost / d.budget : 0;
    return [x, y, Number(ratio.toFixed(2))]; // [column index, row index, usage ratio]
  });

  // Highcharts heatmap configuration
  const options: Highcharts.Options = {
    chart: {
      type: "heatmap",
      height: "60%",
    },
    title: {
      text: "Category-Type Usage Heatmap",
    },
    xAxis: {
      categories: types,
      title: { text: "Type" }
    },
    yAxis: {
      categories: categories,
      title: { text: "Category" },
      reversed: true
    },
    colorAxis: {
        min: 0,
        max: 2,
        stops: [
            [0.0, "#00FF00"],    // 0
            [0.25, "#00FF00"],   // 50%
            [0.4, "#FFFF00"],    // 80%
            [0.5, "#FFA500"],    // 100%
            [0.75, "#FF0000"],   // 150%
            [1.0, "#A020F0"]     // max 200% (purple)
        ]
    },
    legend: {
      align: "right",
      layout: "vertical",
      verticalAlign: "middle",
      y: 25,
      symbolHeight: 300,
      title: {
        text: "Usage Ratio"
      }
    },
    tooltip: {
      formatter: function () {
        const point = this.point as Highcharts.Point;
        const ratio = point.value ?? 0;
        const x = point.x as number;
        const y = point.y as number;

        // Determine usage level label
        let level = "";
        if (ratio < 0.5) level = "Underused (<50%)";
        else if (ratio < 0.8) level = "Normal (50–80%)";
        else if (ratio < 1.0) level = "Overused (80–100%)";
        else if (ratio < 1.5) level = "Heavily Overused (100–150%)";
        else level = "Critically Overused (>150%)";

        return `
          <b>${categories[y]} - ${types[x]}</b><br/>
          Cost: ${currencyFormatter(data.find(d => d.category === categories[y] && d.type === types[x])?.cost ?? 0)}<br/>
          Budget: ${currencyFormatter(data.find(d => d.category === categories[y] && d.type === types[x])?.budget ?? 0)}<br/>
          Usage: ${(ratio * 100).toFixed(1)}%<br/>
          Level: <span style="font-weight:600">${level}</span>
        `;
      }
    },
    series: [{
      name: "Cost / Budget Ratio",
      borderWidth: 1,
      data: heatmapData,
      dataLabels: {
        enabled: true,
        color: "#000",
        format: "{point.value:.2f}"
      },
      type: "heatmap"
    }],
    credits: {
      enabled: false,
    }
  };

    // User Feedback
    const { instance } = useMsal();
    const { data: flights } = useFlights();
    const account = instance.getActiveAccount();
    const [isFeedbackPanelOpen, { setTrue: openFeedbackPanel, setFalse: dismissFeedbackPanel }] = useBoolean(false);
    const [currentURL, setCurrentURL] = useState<string>('');
    const [currentScreenshotURL, setCurrentScreenshotURL] = useState<string>('');

    const captureScreenshot = async (title: string) => {
        trackEventCallback(LogComponent.BudgetTracking, LogElement.BudgetTrackingServiceLayout, "Send Feedback Chart", LogTarget.Button, undefined, EventType.Click);

        const url = window.location.href;
        setCurrentURL(url);

        await openFeedbackPanel();

        const targetElement = document.getElementById(`FeedbackSection_${title}`);

        if (targetElement) {
            setTimeout(() => {
                html2canvas(targetElement).then((canvas) => {
                    const screenshotDataUrl = canvas.toDataURL('image/png');
                    setCurrentScreenshotURL(screenshotDataUrl);
                });
            }, 100);

        }
    };

    const handleDismissFeedbackPanel = () => {
        setCurrentScreenshotURL('');
        dismissFeedbackPanel();
    };

    const categoryGroupedData = categories.map(category => {
        const typesForCategory = data
            .filter(d => (d.category ?? "Unknown") === category)
            .map(d => d.type ?? "Unknown");
        return { category, types: Array.from(new Set(typesForCategory)) };
    });

    const handleDrillDownSelect = (key: string) => {
        const [category, type] = key.split(":");
        onDrilldownSelect?.({ category, type });
    };

    const selectedData = useMemo(() => {
        if (!selected) return [];
        return data.filter(
            d => d.category === selected.category && d.type === selected.type
        );
    }, [selected, data]);

  return (
    <div className={styles.heatmapContainer} id={`FeedbackSection_FinOpsCategoryHeatmap`} >
        <DrillDownMenu
            data={categoryGroupedData}
            onSelect={handleDrillDownSelect}
        />
        {flights?.enableUserFeedback &&
            <IconButton
                title="Send feedback"
                className={styles.feedbackIcon}
                iconProps={feedbackIconProps}
                onClick={() => captureScreenshot("FinOpsCategoryHeatmap")}
        />}
        
        <Section headerProps={{ title: "Category-Type Usage Heatmap" }} styles={{ content: "" }} loading={isloadingBudgetData}></Section>
        <HighchartsReact highcharts={Highcharts} options={options} />
        <FeedbackPanel
            isPanelOpen={isFeedbackPanelOpen}
            onDismiss={handleDismissFeedbackPanel}
            userName={account?.username}
            currentURL={currentURL}
            screenshotInputURL={currentScreenshotURL}
            FeedbackTab={LogComponent.BudgetTracking}
        />
    </div>
  );
};

export default CategoryTypeHeatmap;
